import React from 'react';

import PropTypes from 'prop-types';

import Chart from 'wanderu-component-chart';
import {
    MarkdownContent,
    Section,
    Typography,
} from 'wanderu-components';
import { localize } from 'wanderu-i18n';

import styles from './PriceChart.scss';

function PriceChart(props) {
    const {
        t,
        i18nMoney,
        i18nMoment,
        priceHistory,
        namespace,
        cheapestPrice,
        savingsPriceUsd,
    } = props;
    const {
        daily,
        monthly,
        metrics = {},
        daysUntilDepart,
        xTickValuesDaily,
        xTickValuesMonthly,
        xTickValuesDepartDays,
    } = priceHistory;
    const {
        highPriceMonth,
        highPriceMonthPrice,
        lowPriceMonth,
        lowPriceMonthPrice,
        lowUpcomingMonthPrice,
        highUpcomingMonthPrice,
        lowUpcomingMonthPriceDate,
        lowUpcomingWeekDate,
        highUpcomingWeekDate,
        lowUpcomingWeekPrice,
        highUpcomingWeekPrice,
        commonMonth,
        commonWeekPrice,
        commonMonthPrice,
    } = metrics;

    const defaultOpts = {
        maximumFractionDigits: 0,
        minimumFractionDigits: 0,
    };

    let pricingInfo;

    if (!!cheapestPrice && !!savingsPriceUsd) {
        pricingInfo = {
            price: i18nMoney(cheapestPrice).format(),
            savingsPrice: i18nMoney(savingsPriceUsd).format(),
            lowUpcomingWeekPrice: lowUpcomingWeekPrice
                ? i18nMoney(lowUpcomingWeekPrice).format(defaultOpts)
                : null,
            lowUpcomingWeekDate: lowUpcomingWeekDate
                ? i18nMoment(lowUpcomingWeekDate).format('LL')
                : null,
            lowUpcomingMonthPriceDate: lowUpcomingMonthPriceDate
                ? i18nMoment(lowUpcomingMonthPriceDate).format('LL')
                : null,
            lowUpcomingMonthPrice: lowUpcomingMonthPrice
                ? i18nMoney(lowUpcomingMonthPrice).format(defaultOpts)
                : null,
            lowPriceMonthPrice: lowPriceMonthPrice
                ? i18nMoney(lowPriceMonthPrice).format(defaultOpts)
                : null,
            lowPriceMonth: lowPriceMonth
                ? i18nMoment(lowPriceMonth, 'M').format('MMMM')
                : null,
            highUpcomingWeekPrice: highUpcomingWeekPrice
                ? i18nMoney(highUpcomingWeekPrice).format(defaultOpts)
                : null,
            highUpcomingWeekDate: highUpcomingWeekDate
                ? i18nMoment(highUpcomingWeekDate).format('LL')
                : null,
            highUpcomingMonthPrice: highUpcomingMonthPrice
                ? i18nMoney(highUpcomingMonthPrice).format(defaultOpts)
                : null,
            highPriceMonthPrice: highPriceMonthPrice
                ? i18nMoney(highPriceMonthPrice).format(defaultOpts)
                : null,
            highPriceMonth: highPriceMonth
                ? i18nMoment(highPriceMonth, 'M').format('MMMM')
                : null,
            commonWeekPrice: commonWeekPrice
                ? i18nMoney(commonWeekPrice).format(defaultOpts)
                : null,
            commonMonthPrice: commonMonthPrice
                ? i18nMoney(commonMonthPrice).format(defaultOpts)
                : null,
            commonMonth: commonMonth
                ? i18nMoment(commonMonth, 'M').format('MMMM')
                : null,
        };
    }

    return (
        <Section aria-labelledby="price-chart-header">
            <Typography
                component="h2"
                id="price-chart-header"
                variant="h3"
            >
                {pricingInfo
                    ? t(`${namespace}:cheapestTicketsTitle`)
                    : t(`${namespace}:priceChartSectionTitle`)}
            </Typography>

            {!!pricingInfo && (
                <Typography
                    className={styles.mainContentBody}
                    component={MarkdownContent}
                    paragraph
                >
                    {t(`${namespace}:cheapestTicketsDescription`, pricingInfo)}
                </Typography>
            )}
            <div className={styles.charts}>
                {!!daily && (
                    <>
                        <Chart
                            className={styles.chart}
                            data={daily}
                            headerClassName={styles.chartTitle}
                            numVerticalTicks={3}
                            title={t(`${namespace}:priceDayTitle`)}
                            xTickValues={xTickValuesDaily}
                        />

                        {!!pricingInfo && (
                            <Typography
                                className={styles.mainContentBody}
                                component={MarkdownContent}
                                paragraph
                            >
                                {t(`${namespace}:priceDayDescription`, pricingInfo)}
                            </Typography>
                        )}
                    </>
                )}
                <div className={styles.chartGroup}>
                    {!!monthly && (
                        <div className={styles.chartSubGroup}>
                            <Chart
                                className={styles.chart}
                                data={monthly}
                                headerClassName={styles.chartTitle}
                                title={t(`${namespace}:priceMonthTitle`)}
                                xTickValues={xTickValuesMonthly}
                            />

                            {!!pricingInfo && (
                                <Typography
                                    className={styles.mainContentBody}
                                    component={MarkdownContent}
                                    paragraph
                                >
                                    {t(`${namespace}:priceMonthDescription`, pricingInfo)}
                                </Typography>
                            )}
                        </div>
                    )}
                    {!!daysUntilDepart && (
                        <div className={styles.chartSubGroup}>
                            <Chart
                                chartType="curve"
                                className={styles.chart}
                                data={daysUntilDepart}
                                headerClassName={styles.chartTitle}
                                labelType="number"
                                title={t(`${namespace}:priceDateTitle`)}
                                xTickValues={xTickValuesDepartDays}
                            />

                            {!!pricingInfo && (
                                <Typography
                                    className={styles.mainContentBody}
                                    component={MarkdownContent}
                                    paragraph
                                >
                                    {t(`${namespace}:priceDateDescription`, pricingInfo)}
                                </Typography>
                            )}
                        </div>
                    )}
                </div>
            </div>
        </Section>
    );
}

PriceChart.propTypes = {
    cheapestPrice: PropTypes.number,
    namespace: PropTypes.string.isRequired,
    priceHistory: PropTypes.shape({
        daily: PropTypes.arrayOf(PropTypes.object),
        monthly: PropTypes.arrayOf(PropTypes.object),
        daysUntilDepart: PropTypes.arrayOf(PropTypes.object),
        xTickValuesDaily: PropTypes.array,
        xTickValuesMonthly: PropTypes.array,
        xTickValuesDepartDays: PropTypes.array,
    }).isRequired,
    savingsPriceUsd: PropTypes.number,
};

export { PriceChart };
export default localize(PriceChart);
